import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { fetchRepairReportDetail } from '#/api/aftersalesMgmt/repairReport/fetchDetail';
import { updateRepairReport } from '#/api/aftersalesMgmt/repairReport/update';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import RepairReportEditReferenceSections from
  '#/modules/crmMgmt/aftersales/repairReport/components/RepairReportEditReferenceSections';
import RepairReportInspectionItemsEditor from
  '#/modules/crmMgmt/aftersales/repairReport/components/RepairReportInspectionItemsEditor';
import RepairReportStatusBadge from
  '#/modules/crmMgmt/aftersales/repairReport/components/RepairReportStatusBadge';
import {
  getRepairReportApiErrorMessage,
  isRepairReportVersionConflict,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/apiError';
import {
  buildRepairReportWritePayload,
  createEmptyInspectionItem,
  mapRepairReportDetailToFormValues,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/formSerialize';
import { canEditRepairReport } from '#/modules/crmMgmt/aftersales/repairReport/helper/repairReportStatus';
import {
  REPAIR_REPORT_FIELD_LABELS,
  repairReportFormSchema,
} from '#/modules/crmMgmt/aftersales/repairReport/schemas/repairReportSchema';
import CreateEditReferenceRail from '#/modules/infra/layout/CreateEditReferenceRail';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  repairReportDetailQueryKey,
  repairReportListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  repairDate: '',
  resolution: '',
  remark: '',
  inspectionConclusion: '',
  inspectionItems: [createEmptyInspectionItem()],
};

/**
 * 維修報告編輯（獨立頁；草稿建立由報修明細一鍵完成，本頁只 PATCH）。
 */
const CreateEditRepairReportPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: repairReportDetailQueryKey(id),
    queryFn: () => fetchRepairReportDetail(id),
    enabled: Boolean(id),
    retry: false,
  });

  const { isPending, save } = useCreateEditSave({
    id,
    createFn: async () => {
      throw new Error('請自報修明細建立維修報告');
    },
    updateFn: updateRepairReport,
    buildPayload: (values) => buildRepairReportWritePayload(values, {
      updatedAt: updatedAtRef.current,
    }),
    listQueryKeyPrefix: repairReportListQueryKeyPrefix,
    detailQueryKey: repairReportDetailQueryKey,
    getDetailPath: ROUTES.repairReportDetail,
    afterUpdateNavigateTo: id ? ROUTES.repairReportDetail(id) : null,
    getErrorMessage: (error) => getRepairReportApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isRepairReportVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(repairReportFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }
    updatedAtRef.current = detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapRepairReportDetailToFormValues(detail));
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const detail = detailQuery.data?.data;
  const isPageLoading = Boolean(id) && detailQuery.isLoading;
  const isDetailNotFound = Boolean(id) && detailQuery.isError;
  const isReadOnlyEdit = Boolean(detail) && !canEditRepairReport(detail.status);
  const canShowForm = Boolean(id)
    && !isPageLoading
    && !isDetailNotFound
    && !isReadOnlyEdit;
  const canShowEditRail = canShowForm && Boolean(detail);

  const formActions = (
    <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
      <Button type="button" variant="outline" asChild>
        <Link to={ROUTES.repairReportDetail(id)}>取消</Link>
      </Button>
      <Button type="submit" disabled={isPending}>
        {isPending ? '儲存中…' : '儲存'}
      </Button>
    </div>
  );

  const formInner = (
    <>
      <section className="pb-6">
        <div className="form-block">
          <h2 className="form-block__title">報告內容</h2>
        </div>
        <div className="flex flex-col gap-3">
          <FloatingDateInput
            id="repairDate"
            label="維修日"
            control={control}
            errors={errors}
            placeholder="西元年/月/日"
          />
          <div
            className={cn(
              'grid grid-cols-1 gap-3',
              '@4xl/sheet:grid-cols-2',
            )}
          >
            <FloatingTextarea
              id="resolution"
              label="處理方式"
              register={register}
              errors={errors}
              rows={2}
            />
            <FloatingTextarea
              id="inspectionConclusion"
              label="檢測結論"
              register={register}
              errors={errors}
              rows={2}
            />
          </div>
          <FloatingTextarea
            id="remark"
            label="備註"
            register={register}
            errors={errors}
            rows={2}
          />
        </div>
      </section>

      <RepairReportInspectionItemsEditor
        control={control}
        register={register}
        errors={errors}
      />
    </>
  );

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div
          className={cn(
            canShowEditRail
              ? 'flex min-h-0 w-full flex-1 flex-col'
              : 'mx-auto w-full max-w-6xl',
          )}
        >
          <AftersalesOpsPageHeader
            page="repairReport"
            variant="form"
            title="編輯維修報告"
            statusSlot={
              detail?.status
                ? <RepairReportStatusBadge status={detail.status} size="sm" />
                : null
            }
            trailing={id ? (
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.repairReportDetail(id)}>返回詳情</Link>
              </Button>
            ) : null}
          />

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getRepairReportApiErrorMessage(detailQuery.error, '找不到維修報告')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <RepairReportStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.repairReportDetail(id)}>返回詳情</Link>
              </Button>
            </div>
          ) : null}

          {canShowEditRail ? (
            <CreateEditReferenceRail
              className="min-h-0 flex-1"
              main={(
                <>
                  <FormValidationSummary
                    show={isSubmitAttempted}
                    errors={errors}
                    fieldLabels={REPAIR_REPORT_FIELD_LABELS}
                    customMessages={customMessages}
                  />
                  <form
                    className="sheet create_edit create_edit_form create_edit_form--fill"
                    onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                    noValidate
                  >
                    <div className="create_edit_form__body divide-y divide-gray-500">
                      {formInner}
                    </div>
                    {formActions}
                  </form>
                </>
              )}
              reference={<RepairReportEditReferenceSections detail={detail} />}
            />
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditRepairReportPage;
